// Chutes (UX §6.4): flat top-down steel troughs; the animated chevrons are a separate overlay frame
// (`chev.<dir>.<phase>`, one global clock, §2.9) that the renderer puts on the cell centre (M5 decision 5).
// Canonical bodies: straight = in from S, out to N; corner = in from S, out to E, a concentric quarter-annulus
// bend round the cell's INNER corner (100,100): the straight cross-section x maps to radius r = 100 − x, so
// every layer meets the straight pieces exactly at the cell edges (the centreline r = 50 runs from edge
// midpoint to edge midpoint, cutting the cell centre's corner). The 12 entry × exit frames are these two, rotated in 90° steps and mirrored (art/generators.ts).
// The trough runs 1 unit past the frame edges so chains read as continuous. Cross-section (units): outline
// 14–18, rail 18–24, steel 24–76 (shade 24–32, the left side seen along the flow), rail 76–82, outline
// 82–86 (on the canonical right-hand bend the shade is on the OUTER side, on the mirrored one the inner). Corner chutes get their own chevron overlay (`chevc.<in><out>.<phase>`, one cell) whose chevrons
// run along the centreline arc.
import type { Ctx2D } from '../ctx.ts';
import type { ArtPalette } from '../palette.ts';
import { line, type Pt, rrect } from '../shapes.ts';

/** Chevron spacing along the trough (units). */
export const CHEVRON_SPACING = 25;

function chevron(c: Ctx2D, x: number, y: number, deg: number, pal: ArtPalette, w: number): void {
  c.save();
  c.translate(x, y);
  c.rotate((deg * Math.PI) / 180);
  const pts: Pt[] = [
    [-11, 6],
    [0, -5],
    [11, 6],
  ];
  c.lineCap = 'round';
  c.lineJoin = 'round';
  c.beginPath();
  line(pts)(c);
  c.strokeStyle = pal.sw('machine.chute').outline;
  c.lineWidth = w * 2.2;
  c.stroke();
  c.beginPath();
  line(pts)(c);
  c.strokeStyle = pal.hex('machine.chute');
  c.lineWidth = w;
  c.stroke();
  c.restore();
}

/** The bend's inner corner (canonical S → E) and its centreline radius. */
const BEND = 100;
const MID_R = 50;

/**
 * The bend band (S → E) between radii `r0` < `r1` round the inner corner (100,100), running `ext` units past
 * both cell edges (straight pieces reach −1…101).
 */
function bend(c: Ctx2D, r0: number, r1: number, ext = 1): void {
  c.beginPath();
  c.moveTo(BEND - r1, BEND + ext);
  c.lineTo(BEND - r1, BEND);
  c.arc(BEND, BEND, r1, Math.PI, Math.PI * 1.5);
  c.lineTo(BEND + ext, BEND - r1);
  c.lineTo(BEND + ext, BEND - r0);
  c.arc(BEND, BEND, r0, Math.PI * 1.5, Math.PI, true);
  c.lineTo(BEND - r0, BEND + ext);
  c.closePath();
}

/**
 * A chute body in cell units (100 = one cell), canonical orientation, no chevrons. `mirrored` (a corner the
 * caller mirrors in x): the shade band stays on the trough's left, seen along the flow, as on the straights.
 */
export function drawChuteBody(
  c: Ctx2D,
  shape: 'straight' | 'corner',
  pal: ArtPalette,
  mirrored = false,
): void {
  const steel = pal.sw('plate.steel');
  const rail = pal.sw('mach.front');
  const outline = pal.hex('mach.outline');
  if (shape === 'straight') {
    c.fillStyle = outline;
    c.fillRect(14, -1, 72, 102);
    c.fillStyle = steel.base;
    c.fillRect(24, -1, 52, 102);
    c.fillStyle = steel.shade;
    c.fillRect(24, -1, 8, 102);
    for (const x of [18, 76]) {
      c.fillStyle = rail.base;
      c.beginPath();
      rrect(x, -2, 6, 104, 0)(c);
      c.fill();
      c.fillStyle = rail.light;
      c.fillRect(x + 1, -2, 1.6, 104);
    }
    return;
  }
  // the straight's x (the distance from the trough's left, seen along the flow) ↦ bend radius: 100 − x on the
  // canonical right-hand bend (its left side is the outer one), x on the mirrored one (left = inner)
  const band = (x0: number, x1: number): [number, number] =>
    mirrored ? [x0, x1] : [100 - x1, 100 - x0];
  for (const [[r0, r1], color, ext] of [
    [band(14, 86), outline, 1],
    [band(18, 82), rail.base, 2],
    [band(24, 76), steel.base, 1],
    [band(24, 32), steel.shade, 1],
    // rail highlights: straight x + 1 … x + 2.6
    [band(19, 20.6), rail.light, 2],
    [band(77, 78.6), rail.light, 2],
  ] as const) {
    bend(c, r0, r1, ext);
    c.fillStyle = color;
    c.fill();
  }
}

/**
 * The corner chevron overlay (one cell, canonical in S → out E): chevrons along the centreline arc (radius 50
 * round the inner corner), each turned along the tangent, every CHEVRON_SPACING arc units with the straight
 * overlay's phase offset measured from the arc midpoint, clipped to the wedge of ±size/2 arc units round the
 * midpoint (the straight overlay's `size`-unit window).
 */
export function drawCornerChevrons(
  c: Ctx2D,
  size: number,
  phase: number,
  phases: number,
  pal: ArtPalette,
  ow: number,
): void {
  const off = (phase * CHEVRON_SPACING) / phases;
  const len = (MID_R * Math.PI) / 2;
  const half = size / 2;
  // the arc angle φ (canvas, round the inner corner) of arc length s from the entry edge: π … 1.5π
  const phi = (s: number): number => Math.PI + s / MID_R;
  const a0 = phi(len / 2 - half);
  const a1 = phi(len / 2 + half);
  const R = 2 * BEND;
  c.save();
  c.beginPath();
  c.moveTo(BEND, BEND);
  c.lineTo(BEND + Math.cos(a0) * R, BEND + Math.sin(a0) * R);
  c.arc(BEND, BEND, R, a0, a1);
  c.closePath();
  c.clip();
  for (let k = -3; k < 4; k++) {
    // relative to the midpoint the same as the straight overlay: 25k − 14 + off (straight: y = 39 − off)
    const d = k * CHEVRON_SPACING - 14 + off;
    if (d < -half - CHEVRON_SPACING || d > half + CHEVRON_SPACING) continue;
    const a = phi(len / 2 + d);
    chevron(
      c,
      BEND + Math.cos(a) * MID_R,
      BEND + Math.sin(a) * MID_R,
      ((a + Math.PI) * 180) / Math.PI,
      pal,
      ow,
    );
  }
  c.restore();
}

/**
 * The chevron overlay (a `size`-unit square on the cell centre), canonical direction N: chevrons every
 * CHEVRON_SPACING units, shifted by phase/phases of a spacing toward N, clipped to the square.
 */
export function drawChevrons(
  c: Ctx2D,
  size: number,
  phase: number,
  phases: number,
  pal: ArtPalette,
  ow: number,
): void {
  const off = (phase * CHEVRON_SPACING) / phases;
  // the same positions as along a whole straight trough (cell y = k·spacing + 39 − off), in overlay units
  const y0 = 50 - size / 2;
  c.save();
  c.beginPath();
  c.rect(0, 0, size, size);
  c.clip();
  for (let k = -2; k < 6; k++) {
    const y = k * CHEVRON_SPACING + 39 - off - y0;
    if (y < -CHEVRON_SPACING || y > size + CHEVRON_SPACING) continue;
    chevron(c, size / 2, y, 0, pal, ow);
  }
  c.restore();
}
